

import { useState } from 'react';
import { Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { format, isToday, isYesterday, isThisYear } from 'date-fns';
import { useItemRevisions, type Revision, type RevisionActivity, countDeltaChanges, getRevisionUserName, formatRevisionTime } from '@/hooks/useRevisions';
import { RevisionDetailSheet } from '@/components/content/RevisionDetailSheet';
import { cn } from '@/lib/utils';

interface RevisionsSidebarDetailProps {
    collection: string;
    itemId: string;
    onRevert?: (data: Record<string, any>) => void;
}

export function RevisionsSidebarDetail({
    collection,
    itemId,
    onRevert,
}: RevisionsSidebarDetailProps) {
    const { data: revisions = [], isLoading: loading } = useItemRevisions(collection, itemId);
    const [selectedRevision, setSelectedRevision] = useState<Revision | null>(null);
    const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set(['today']));

    // Group revisions by date
    const revisionsByDate = revisions.reduce((groups, revision) => {
        const activity = revision.activity as RevisionActivity;
        if (typeof activity === 'number') return groups;

        const date = new Date(activity.timestamp);
        let dateKey: string;
        let dateLabel: string;

        if (isToday(date)) {
            dateKey = 'today';
            dateLabel = 'Today';
        } else if (isYesterday(date)) {
            dateKey = 'yesterday';
            dateLabel = 'Yesterday';
        } else if (isThisYear(date)) {
            dateKey = format(date, 'MMM d');
            dateLabel = format(date, 'MMM d');
        } else {
            dateKey = format(date, 'MMM d, yyyy');
            dateLabel = format(date, 'MMM d, yyyy');
        }

        if (!groups[dateKey]) {
            groups[dateKey] = { label: dateLabel, revisions: [] };
        }
        groups[dateKey]?.revisions.push(revision);
        return groups;
    }, {} as Record<string, { label: string; revisions: Revision[] }>);

    const toggleDate = (dateKey: string) => {
        setExpandedDates(prev => {
            const next = new Set(prev);
            if (next.has(dateKey)) {
                next.delete(dateKey);
            } else {
                next.add(dateKey);
            }
            return next;
        });
    };

    const handleRevisionClick = (revision: Revision) => {
        setSelectedRevision(revision);
    };

    const handleRevert = (data: Record<string, any>) => {
        onRevert?.(data);
        setSelectedRevision(null);
    };

    // Find the previous revision (the one before the selected revision in chronological order)
    const getPreviousRevision = (currentRevision: Revision | null): Revision | null => {
        if (!currentRevision || revisions.length === 0) return null;

        const currentIndex = revisions.findIndex(r => r.id === currentRevision.id);
        if (currentIndex === -1 || currentIndex === revisions.length - 1) return null;

        // Since revisions are sorted newest first, the previous one is at index + 1
        return revisions[currentIndex + 1] || null;
    };

    return (
        <>
            <>
                {loading ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                ) : revisions.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4 italic">
                        No revisions
                    </p>
                ) : (
                    <ScrollArea className="h-[400px]">
                        <div className="space-y-2">
                            {Object.entries(revisionsByDate).map(([dateKey, { label, revisions: dateRevisions }]) => (
                                <div key={dateKey} className="space-y-1">
                                    {/* Date Header */}
                                    <button
                                        onClick={() => toggleDate(dateKey)}
                                        className="flex items-center gap-2 w-full px-2 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                                    >
                                        {expandedDates.has(dateKey) ? (
                                            <ChevronDown className="h-4 w-4" />
                                        ) : (
                                            <ChevronRight className="h-4 w-4" />
                                        )}
                                        <span>{label}</span>
                                    </button>

                                    {/* Revisions in this date group */}
                                    {expandedDates.has(dateKey) && (
                                        <div className="ml-2 space-y-px">
                                            {dateRevisions.map((revision, index) => (
                                                <RevisionItem
                                                    key={revision.id}
                                                    revision={revision}
                                                    isLast={index === dateRevisions.length - 1}
                                                    onClick={() => handleRevisionClick(revision)}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </ScrollArea>
                )}
            </>

            <RevisionDetailSheet
                revision={selectedRevision}
                previousRevision={getPreviousRevision(selectedRevision)}
                collection={collection}
                open={!!selectedRevision}
                onOpenChange={(open) => !open && setSelectedRevision(null)}
                onRevert={handleRevert}
            />
        </>
    );
}

interface RevisionItemProps {
    revision: Revision;
    isLast: boolean;
    onClick: () => void;
}

function RevisionItem({ revision, isLast, onClick }: RevisionItemProps) {
    const activity = revision.activity as RevisionActivity;

    if (typeof activity === 'number') return null;

    const userName = getRevisionUserName(activity);
    const { relative } = formatRevisionTime(activity.timestamp);
    const changesCount = countDeltaChanges(revision.delta);
    const action = activity.action.toLowerCase();

    // Get header message
    const getHeaderMessage = () => {
        switch (action) {
            case 'create':
                return changesCount > 0 ? `Created with ${changesCount} field${changesCount !== 1 ? 's' : ''}` : 'Created';
            case 'update':
                return changesCount > 0 ? `Updated ${changesCount} field${changesCount !== 1 ? 's' : ''}` : 'Updated';
            case 'delete':
                return 'Deleted';
            case 'revert':
                return 'Reverted';
            default:
                return 'Changed';
        }
    };

    // Get border color
    const getBorderColor = () => {
        switch (action) {
            case 'create':
                return 'border-green-500';
            case 'update':
                return 'border-blue-500';
            case 'delete':
                return 'border-red-500';
            case 'revert':
                return 'border-amber-500';
            default:
                return 'border-gray-400';
        }
    };

    return (
        <button
            onClick={onClick}
            className={cn(
                "relative w-full px-3 py-2 text-left hover:bg-muted/50 transition-colors rounded-sm border-l-2 pl-4",
                getBorderColor(),
                !isLast && "mb-3"
            )}
        >
            <div className="space-y-0.5">
                <p className="text-sm font-semibold">{getHeaderMessage()}</p>
                <p className="text-xs text-muted-foreground">
                    {relative} – {userName}
                </p>
            </div>
        </button>
    );
}
